/**
 * ClaimsDashboard - Tabbed view for Received Claims + Sent Claims
 *
 * Tab 1 — Received Claims: Agent sees all claims received across broadcast leads.
 *         Grouped by lead → offers → claims. Can approve/reject claims.
 *
 * Tab 2 — Sent Claims: Employee sees claims they sent, grouped by lead → offers.
 *         Can track status, view offer details, withdraw pending claims.
 *
 * Both tabs use the same progressive disclosure pattern:
 *   Collapsed lead card → Expanded with rich offer cards + claims
 *
 * Route: /leads/received-claims
 * Who sees it: Any user with company_id
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Message } from 'primereact/message';
import { Paginator } from 'primereact/paginator';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Skeleton } from 'primereact/skeleton';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { TabView, TabPanel } from 'primereact/tabview';
import { leadBroadcastService, getClaimStatusConfig, formatCurrency, formatLoanAmount } from '../../services/leadBroadcastService';
import { processingDecision, getProcessingHistory, getReasonLabel, getProcessingStatusConfig } from '../../services/leadProcessingService';
import { getProductLoanTypes } from '../../services/offerCalculationService';
import ProcessingWorkflowPanel from '../../components/Leads/ProcessingWorkflowPanel';
import StatusUpdateDialog from '../../components/Leads/StatusUpdateDialog';
import { createApplication } from '../../services/leadProcessingService';
import ProcessingHistoryTimeline from '../../components/Leads/ProcessingHistoryTimeline';
import OfferStatusBadge from '../../components/Leads/OfferStatusBadge';
import RegenerateOffersDialog from '../../components/Leads/company/RegenerateOffersDialog';
import OffersComparison from '../../components/Offers/OffersComparison';

const ITEMS_PER_PAGE = 20;

/** Get staleness info for a broadcast based on last offer generation date */
const getOffersAge = (broadcast) => {
    const history = broadcast.regeneration_history;
    const lastDate = history?.length
        ? history[history.length - 1]?.generated_at
        : broadcast.broadcast_date || broadcast.created_at;

    if (!lastDate) return null;

    const days = Math.floor((Date.now() - new Date(lastDate).getTime()) / (1000 * 60 * 60 * 24));

    if (days <= 7) return { days, severity: 'success', label: 'Fresh', icon: 'pi-check-circle' };
    if (days <= 30) return { days, severity: 'warning', label: `${days}d old`, icon: 'pi-clock' };
    return { days, severity: 'danger', label: `${days}d old`, icon: 'pi-exclamation-triangle' };
};
const MAX_VISIBLE_OFFERS = 3;

const PRODUCT_TYPE_MAP = {
    'new_personal_loan': 'New Personal Loan',
    'personal_loan_top_up': 'Personal Loan Top Up',
    'personal_loan_balance_transfer': 'Personal Loan Balance Transfer',
    'personal_loan_bt_top_up': 'Personal Loan BT + Top Up',
    'personal_loan_over_draft': 'Personal Loan Over Draft',
    'new_home_loan': 'New Home Loan',
    'home_loan_top_up': 'Home Loan Top Up',
    'home_loan_balance_transfer': 'Home Loan Balance Transfer',
    'home_loan_bt_top_up': 'Home Loan BT + Top Up',
    'home_loan_over_draft': 'Home Loan Over Draft',
    'home_loan_vendor_bt': 'Home Loan Vendor BT',
    'home_loan_assignment': 'Home Loan Assignment',
    'loan_against_property': 'Loan Against Property',
    'lap_top_up': 'LAP Top Up',
    'lap_balance_transfer': 'LAP Balance Transfer',
    'lap_bt_top_up': 'LAP BT + Top Up',
    'lap_over_draft': 'LAP Over Draft',
    'lap_vendor_bt': 'LAP Vendor BT',
    'car_loan': 'Car Loan',
    'car_over_draft': 'Car Over Draft',
    'educational_loan': 'Educational Loan',
    'equipment_loan': 'Equipment Loan',
};

const formatProductType = (type) => PRODUCT_TYPE_MAP[type] || type || 'Not specified';

const STATUS_FILTER_OPTIONS = [
    { label: 'All Claims', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Rejected', value: 'rejected' },
    { label: 'Withdrawn', value: 'withdrawn' },
];

// ══════════════════════════════════════════
// SHARED RENDER HELPERS
// ══════════════════════════════════════════

const renderClaimStatusTag = (status) => {
    const config = getClaimStatusConfig(status);
    return <Tag value={config.label} severity={config.severity} className="text-xs" />;
};

const renderSummaryStats = (summary) => (
    <div className="flex flex-wrap align-items-center gap-2 mb-2">
        <span className="flex align-items-center gap-1 text-xs text-600 surface-100 border-round px-2 py-1">
            <i className="pi pi-list" style={{ fontSize: '0.7rem' }} /> Total <strong>{summary.total}</strong>
        </span>
        {summary.pending > 0 && (
            <span className="flex align-items-center gap-1 text-xs border-round px-2 py-1" style={{ backgroundColor: 'var(--yellow-50, #fffbeb)', color: 'var(--yellow-700)' }}>
                <i className="pi pi-clock" style={{ fontSize: '0.7rem' }} /> Pending <strong>{summary.pending}</strong>
            </span>
        )}
        {summary.approved > 0 && (
            <span className="flex align-items-center gap-1 text-xs border-round px-2 py-1" style={{ backgroundColor: 'var(--green-50, #ecfdf5)', color: 'var(--green-700)' }}>
                <i className="pi pi-check-circle" style={{ fontSize: '0.7rem' }} /> Approved <strong>{summary.approved}</strong>
            </span>
        )}
        {summary.rejected > 0 && (
            <span className="flex align-items-center gap-1 text-xs border-round px-2 py-1" style={{ backgroundColor: 'var(--red-50, #fef2f2)', color: 'var(--red-700)' }}>
                <i className="pi pi-times-circle" style={{ fontSize: '0.7rem' }} /> Rejected <strong>{summary.rejected}</strong>
            </span>
        )}
        {summary.withdrawn > 0 && (
            <span className="flex align-items-center gap-1 text-xs text-500 surface-100 border-round px-2 py-1">
                <i className="pi pi-undo" style={{ fontSize: '0.7rem' }} /> Withdrawn <strong>{summary.withdrawn}</strong>
            </span>
        )}
    </div>
);


const renderSkeleton = () => (
    <div>
        {[1, 2, 3].map((i) => (
            <div key={i} className="surface-card border-round shadow-1 p-3 mb-2">
                <div className="flex align-items-center justify-content-between">
                    <div className="flex align-items-center gap-3">
                        <Skeleton shape="circle" size="2.5rem" />
                        <div>
                            <Skeleton width="10rem" height="1rem" className="mb-2" />
                            <Skeleton width="15rem" height="0.75rem" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <Skeleton width="5rem" height="1.5rem" borderRadius="1rem" />
                        <Skeleton width="4rem" height="1.5rem" borderRadius="1rem" />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════

const ReceivedClaims = () => {
    const toast = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = searchParams.get('tab') === 'sent' ? 1 : 0;
    const [activeTab, setActiveTabRaw] = useState(initialTab);
    const setActiveTab = (tab) => {
        setActiveTabRaw(tab);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (tab === 1) next.set('tab', 'sent');
            else next.delete('tab');
            return next;
        }, { replace: true });
    };

    // Status update dialog (for processor actions in Sent tab)
    const [statusDialogVisible, setStatusDialogVisible] = useState(false);
    const [statusDialogLeadId, setStatusDialogLeadId] = useState(null);
    const [statusDialogCurrentStatus, setStatusDialogCurrentStatus] = useState(null);
    const [statusDialogPreselect, setStatusDialogPreselect] = useState(null);
    const [statusDialogBankId, setStatusDialogBankId] = useState(null);
    const [statusDialogBankName, setStatusDialogBankName] = useState(null);
    const [statusDialogOfferBanks, setStatusDialogOfferBanks] = useState([]);
    const [startProcessingLoading, setStartProcessingLoading] = useState(false);

    // Regenerate offers dialog
    const [regenVisible, setRegenVisible] = useState(false);
    const [regenBroadcast, setRegenBroadcast] = useState(null);

    // Detect mobile
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 576);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Shared: loan types for product filter
    const [loanTypes, setLoanTypes] = useState([]);
    useEffect(() => {
        getProductLoanTypes().then(res => {
            if (res.success) setLoanTypes(res.data || []);
        }).catch(() => {});
    }, []);

    // ══════════════════════════════════════════
    // RECEIVED CLAIMS STATE
    // ══════════════════════════════════════════
    const [rcData, setRcData] = useState([]);
    const [rcSummary, setRcSummary] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, withdrawn: 0 });
    const [rcLoading, setRcLoading] = useState(true);
    const [rcExpanded, setRcExpanded] = useState({});
    const [rcOfferIdx, setRcOfferIdx] = useState({});
    const [rcFirst, setRcFirst] = useState(0);
    const [rcTotal, setRcTotal] = useState(0);
    const [rcFilter, setRcFilter] = useState('all');
    const [rcLoanTypeFilter, setRcLoanTypeFilter] = useState(null);
    const [rcSearch, setRcSearch] = useState('');
    const [rcSearchInput, setRcSearchInput] = useState('');
    const [rcDateFrom, setRcDateFrom] = useState(null);
    const [rcDateTo, setRcDateTo] = useState(null);

    // Approve dialog
    const [approveDialogVisible, setApproveDialogVisible] = useState(false);
    const [approvingClaim, setApprovingClaim] = useState(null);
    const [approvingBroadcast, setApprovingBroadcast] = useState(null);
    const [approvingOffer, setApprovingOffer] = useState(null);
    const [approving, setApproving] = useState(false);

    // Reject dialog
    const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
    const [rejectingClaim, setRejectingClaim] = useState(null);
    const [rejectingOffer, setRejectingOffer] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [rejecting, setRejecting] = useState(false);

    // Make Processor — inline confirm
    const [processorConfirmTarget, setProcessorConfirmTarget] = useState(null); // { claim, broadcast, offer }
    const [changingProcessor, setChangingProcessor] = useState(false);

    // ══════════════════════════════════════════
    // SENT CLAIMS STATE
    // ══════════════════════════════════════════
    const [scData, setScData] = useState([]);
    const [scSummary, setScSummary] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, withdrawn: 0 });
    const [scLoading, setScLoading] = useState(false);
    const [scLoaded, setScLoaded] = useState(false); // lazy load — only fetch when tab is first opened
    const [scExpanded, setScExpanded] = useState({});
    const [scOfferIdx, setScOfferIdx] = useState({});
    const [scFirst, setScFirst] = useState(0);
    const [scTotal, setScTotal] = useState(0);
    const [scFilter, setScFilter] = useState('all');
    const [scLoanTypeFilter, setScLoanTypeFilter] = useState(null);
    const [scSearch, setScSearch] = useState('');
    const [scSearchInput, setScSearchInput] = useState('');
    const [scDateFrom, setScDateFrom] = useState(null);
    const [scDateTo, setScDateTo] = useState(null);

    // Withdraw dialog
    const [withdrawDialogVisible, setWithdrawDialogVisible] = useState(false);
    const [withdrawingClaim, setWithdrawingClaim] = useState(null);
    const [withdrawingOffer, setWithdrawingOffer] = useState(null);
    const [withdrawing, setWithdrawing] = useState(false);

    // ── Processing Decision State (Agent) ──
    const [decisionDialogVisible, setDecisionDialogVisible] = useState(false);
    const [decisionBroadcast, setDecisionBroadcast] = useState(null);
    const [decisionType, setDecisionType] = useState(null); // 'close'
    const [decidingLoading, setDecidingLoading] = useState(false);

    // ── Processing History (Sent tab) ──
    const [processingHistoryMap, setProcessingHistoryMap] = useState({});

    // ══════════════════════════════════════════
    // DATA LOADING
    // ══════════════════════════════════════════

    /** Format date to YYYY-MM-DD for API */
    const formatDateParam = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    // Cross-tab counts — always fresh from the last API response
    const [crossTabCounts, setCrossTabCounts] = useState({ received_total: 0, sent_total: 0 });

    const loadReceivedClaims = useCallback(async (page = 1, overrides = {}) => {
        setRcLoading(true);
        try {
            const filters = {
                page,
                limit: ITEMS_PER_PAGE,
                status: overrides.status ?? rcFilter,
                search: overrides.search ?? rcSearch,
                date_from: formatDateParam(overrides.date_from !== undefined ? overrides.date_from : rcDateFrom),
                date_to: formatDateParam(overrides.date_to !== undefined ? overrides.date_to : rcDateTo),
                loan_type_id: rcLoanTypeFilter || undefined,
            };
            const result = await leadBroadcastService.getReceivedClaims(filters);
            if (result.success) {
                setRcData(result.data?.broadcasts || []);
                setRcSummary(result.data?.summary || { total: 0, pending: 0, approved: 0, rejected: 0, withdrawn: 0 });
                setRcTotal(result.data?.total || 0);
                // Update cross-tab counts from response
                if (result.data?.cross_tab_counts) {
                    setCrossTabCounts(result.data.cross_tab_counts);
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to load received claims', life: 3000 });
            }
        } catch (error) {
            console.error('Failed to load received claims:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load received claims.', life: 3000 });
        } finally {
            setRcLoading(false);
        }
    }, [rcFilter, rcSearch, rcDateFrom, rcDateTo, rcLoanTypeFilter]);

    const loadSentClaims = useCallback(async (page = 1, overrides = {}) => {
        setScLoading(true);
        try {
            const filters = {
                page,
                limit: ITEMS_PER_PAGE,
                status: overrides.status ?? scFilter,
                search: overrides.search ?? scSearch,
                date_from: formatDateParam(overrides.date_from !== undefined ? overrides.date_from : scDateFrom),
                date_to: formatDateParam(overrides.date_to !== undefined ? overrides.date_to : scDateTo),
                loan_type_id: scLoanTypeFilter || undefined,
            };
            const result = await leadBroadcastService.getSentClaims(filters);
            if (result.success) {
                setScData(result.data?.broadcasts || []);
                setScSummary(result.data?.summary || { total: 0, pending: 0, approved: 0, rejected: 0, withdrawn: 0 });
                setScTotal(result.data?.total || 0);
                // Update cross-tab counts from response
                if (result.data?.cross_tab_counts) {
                    setCrossTabCounts(result.data.cross_tab_counts);
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to load sent claims', life: 3000 });
            }
        } catch (error) {
            console.error('Failed to load sent claims:', error);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load sent claims.', life: 3000 });
        } finally {
            setScLoading(false);
        }
    }, [scFilter, scSearch, scDateFrom, scDateTo, scLoanTypeFilter]);

    // ── Initial load for Received tab (active on mount) ──
    const rcMounted = useRef(false);
    useEffect(() => {
        if (!rcMounted.current) {
            rcMounted.current = true;
            loadReceivedClaims(1);
            return;
        }
        // Debounce subsequent filter changes
        const timer = setTimeout(() => loadReceivedClaims(1), 300);
        return () => clearTimeout(timer);
    }, [rcFilter, rcSearch, rcDateFrom, rcDateTo, rcLoanTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-switch to Sent tab if Received is empty but Sent has data (helps new employees)
    const autoSwitched = useRef(false);
    useEffect(() => {
        if (!autoSwitched.current && !rcLoading && activeTab === 0 && crossTabCounts.received_total === 0 && crossTabCounts.sent_total > 0) {
            autoSwitched.current = true;
            setActiveTab(1);
        }
    }, [rcLoading, crossTabCounts]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Lazy load Sent tab — only fetch when tab is first opened or filters change ──
    useEffect(() => {
        if (activeTab !== 1) return; // not on Sent tab
        if (!scLoaded) {
            setScLoaded(true);
            loadSentClaims(1);
            return;
        }
        // Debounce filter changes
        const timer = setTimeout(() => loadSentClaims(1), 300);
        return () => clearTimeout(timer);
    }, [activeTab, scFilter, scSearch, scDateFrom, scDateTo, scLoanTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    // ══════════════════════════════════════════
    // RECEIVED CLAIMS ACTIONS
    // ══════════════════════════════════════════

    const openApproveDialog = (claim, broadcast, offer) => {
        setApprovingClaim(claim);
        setApprovingBroadcast(broadcast);
        setApprovingOffer(offer);
        setApproveDialogVisible(true);
    };

    const handleApprove = async () => {
        if (!approvingClaim) return;
        setApproving(true);
        try {
            const result = await leadBroadcastService.approveClaim(approvingClaim.id);
            if (result.success) {
                toast.current?.show({ severity: 'success', summary: 'Approved', detail: `Claim by ${approvingClaim.claimed_by?.name || 'employee'} approved. Full access share created.`, life: 4000 });
                setApproveDialogVisible(false);
                setApprovingClaim(null);
                setApprovingBroadcast(null);
                setApprovingOffer(null);
                loadReceivedClaims(Math.floor(rcFirst / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to approve claim', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to approve claim.', life: 3000 });
        } finally {
            setApproving(false);
        }
    };

    const handleChangeProcessor = async () => {
        if (!processorConfirmTarget) return;
        const { claim, broadcast } = processorConfirmTarget;
        setChangingProcessor(true);
        try {
            const result = await leadBroadcastService.assignProcessor(
                broadcast.broadcast_id,
                claim.claimed_by?.id,
                claim.offer_id
            );
            if (result.success) {
                const name = claim.claimed_by?.name || 'Employee';
                toast.current?.show({ severity: 'success', summary: 'Processor Changed', detail: `${name} is now the processor.`, life: 4000 });
                setProcessorConfirmTarget(null);
                loadReceivedClaims(Math.floor(rcFirst / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to change processor', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to change processor. Please try again.', life: 3000 });
        } finally {
            setChangingProcessor(false);
        }
    };

    const openRejectDialog = (claim, offer) => {
        setRejectingClaim(claim);
        setRejectingOffer(offer);
        setRejectReason('');
        setRejectDialogVisible(true);
    };

    const handleReject = async () => {
        if (!rejectingClaim) return;
        setRejecting(true);
        try {
            const result = await leadBroadcastService.rejectClaim(rejectingClaim.id, rejectReason || null);
            if (result.success) {
                toast.current?.show({ severity: 'info', summary: 'Rejected', detail: `Claim by ${rejectingClaim.claimed_by?.name || 'employee'} rejected.`, life: 3000 });
                setRejectDialogVisible(false);
                setRejectingClaim(null);
                setRejectingOffer(null);
                setRejectReason('');
                loadReceivedClaims(Math.floor(rcFirst / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to reject claim', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to reject claim.', life: 3000 });
        } finally {
            setRejecting(false);
        }
    };

    // ══════════════════════════════════════════
    // SENT CLAIMS ACTIONS
    // ══════════════════════════════════════════

    const openWithdrawDialog = (claim, offer) => {
        setWithdrawingClaim(claim);
        setWithdrawingOffer(offer);
        setWithdrawDialogVisible(true);
    };

    const handleWithdraw = async () => {
        if (!withdrawingClaim) return;
        setWithdrawing(true);
        try {
            const result = await leadBroadcastService.withdrawClaim(withdrawingClaim.id);
            if (result.success) {
                toast.current?.show({ severity: 'info', summary: 'Withdrawn', detail: 'Your claim has been withdrawn.', life: 3000 });
                setWithdrawDialogVisible(false);
                setWithdrawingClaim(null);
                setWithdrawingOffer(null);
                loadSentClaims(Math.floor(scFirst / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed to withdraw claim', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to withdraw claim.', life: 3000 });
        } finally {
            setWithdrawing(false);
        }
    };

    // ══════════════════════════════════════════
    // AGENT PROCESSING DECISIONS
    // ══════════════════════════════════════════

    const openDecisionDialog = (broadcast, type) => {
        setDecisionBroadcast(broadcast);
        setDecisionType(type);
        setDecisionDialogVisible(true);
    };

    const handleProcessingDecision = async () => {
        if (!decisionBroadcast || !decisionType) return;
        setDecidingLoading(true);
        try {
            const result = await processingDecision(decisionBroadcast.broadcast_id, { decision: 'close' });
            if (result.success) {
                toast.current?.show({ severity: 'success', summary: 'Lead Closed', detail: result.message, life: 4000 });
                setDecisionDialogVisible(false);
                setDecisionBroadcast(null);
                loadReceivedClaims(Math.floor(rcFirst / ITEMS_PER_PAGE) + 1);
            } else {
                toast.current?.show({ severity: 'error', summary: 'Error', detail: result.message || 'Failed', life: 3000 });
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.message || 'Failed to close lead', life: 3000 });
        } finally {
            setDecidingLoading(false);
        }
    };

    const loadProcessingHistory = async (broadcastId) => {
        try {
            const result = await getProcessingHistory(broadcastId);
            if (result.success) {
                setProcessingHistoryMap(prev => ({ ...prev, [broadcastId]: result.data }));
            }
        } catch (error) {
            console.error('Failed to load processing history:', error);
        }
    };

    // ══════════════════════════════════════════
    // RECEIVED CLAIMS — CLAIM ROW (per employee)
    // ══════════════════════════════════════════

    const renderReceivedClaimItem = (claim, broadcast, offer) => {
        const isPending = claim.status === 'pending';
        const isApproved = claim.status === 'approved';
        const isCurrentProcessor = isApproved && broadcast.selected_offer_id === claim.offer_id;
        const isProcessorSelected = broadcast.broadcast_status === 'processor_selected';
        const isRetryNeeded = broadcast.processing_status === 'retry_needed';

        // Check if this claim's offer was rejected by bank/processor
        const claimOfferId = claim.offer_id;
        const claimOfferStatus = broadcast.offer_statuses?.[claimOfferId];
        const isOfferRejected = ['rejected', 'bank_rejected', 'customer_rejected', 'processor_rejected'].includes(claimOfferStatus?.status);
        // Only lock the offer for the person who was the rejected processor, not for other claimants
        const isOfferLocked = isOfferRejected && isCurrentProcessor;

        // Can make processor: normal flow OR retry_needed (only for available offers, not current processor)
        const canMakeProcessor = (isProcessorSelected && !isCurrentProcessor && !isOfferLocked)
            || (isRetryNeeded && !isCurrentProcessor && !isOfferLocked && claim.status !== 'rejected' && claim.status !== 'withdrawn');

        return (
            <div
                key={claim.id}
                className="p-3 border-bottom-1 surface-border"
                style={isCurrentProcessor && !isOfferLocked ? { backgroundColor: 'var(--green-50, #ecfdf5)' } : isOfferLocked ? { backgroundColor: 'var(--red-50)', opacity: 0.6 } : {}}
            >
                <div className="flex flex-wrap align-items-start justify-content-between gap-2">
                    <div className="flex align-items-start gap-2 flex-grow-1">
                        <i className={`pi ${isCurrentProcessor ? 'pi-star-fill text-green-500' : 'pi-user'} text-lg mt-1`} style={{ minWidth: '1.25rem' }} />
                        <div>
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <span className="font-semibold text-900">{claim.claimed_by?.name || 'Unknown'}</span>
                                {renderClaimStatusTag(claim.status)}
                                {isCurrentProcessor && <Tag value="Processor" severity="success" className="text-xs" />}
                                {isOfferLocked && <Tag value="Offer Rejected" severity="danger" icon="pi pi-lock" className="text-xs" />}
                            </div>
                            <div className="text-500 text-xs mt-1">
                                Claimed {claim.created_at || 'recently'}
                                {claim.responded_at && (
                                    <span> · {claim.status === 'approved' ? 'Approved' : 'Responded'} {claim.responded_at}{claim.responded_by?.name && ` by ${claim.responded_by.name}`}</span>
                                )}
                            </div>
                            {claim.status === 'rejected' && claim.rejected_reason && <div className="text-red-600 text-xs mt-1">Reason: {claim.rejected_reason}</div>}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 align-items-center flex-shrink-0">
                        {/* Normal flow: Approve + Reject for pending claims */}
                        {isPending && !isProcessorSelected && !isRetryNeeded && (
                            <>
                                <Button label="Approve" icon="pi pi-check" className="p-button-success p-button-outlined p-button-sm" onClick={() => openApproveDialog(claim, broadcast, offer)} />
                                <Button label="Reject" icon="pi pi-times" className="p-button-danger p-button-text p-button-sm" onClick={() => openRejectDialog(claim, offer)} />
                            </>
                        )}
                        {/* Retry flow: Make Processor + Reject for available offers */}
                        {isRetryNeeded && !isOfferLocked && !isCurrentProcessor && isPending && (
                            <>
                                <Button label="Make Processor" icon="pi pi-sync" className="p-button-warning p-button-sm" onClick={() => setProcessorConfirmTarget({ claim, broadcast, offer })} />
                                <Button label="Reject" icon="pi pi-times" className="p-button-danger p-button-text p-button-sm" onClick={() => openRejectDialog(claim, offer)} />
                            </>
                        )}
                        {/* Normal processor switch (not retry) */}
                        {canMakeProcessor && !isRetryNeeded && !isOfferLocked && (
                            <Button label="Make Processor" icon="pi pi-sync" className="p-button-warning p-button-outlined p-button-sm" onClick={() => setProcessorConfirmTarget({ claim, broadcast, offer })} />
                        )}
                    </div>
                </div>
                {/* Employee's claim message — prominent bubble */}
                {claim.claim_message && (
                    <div className="mt-2 ml-5 p-2 border-round surface-100" style={{ borderLeft: '3px solid var(--primary-color)' }}>
                        <div className="flex align-items-center gap-1 mb-1">
                            <i className="pi pi-comment text-primary text-xs" />
                            <span className="text-primary text-xs font-semibold">Message</span>
                        </div>
                        <div className="text-700 text-sm">"{claim.claim_message}"</div>
                    </div>
                )}
            </div>
        );
    };

    // ══════════════════════════════════════════
    // SENT CLAIMS — CLAIM ROW (own claim status)
    // ══════════════════════════════════════════

    const renderSentClaimItem = (claim, broadcast, offer) => {
        const isPending = claim.status === 'pending';
        const isApproved = claim.status === 'approved';
        const isCurrentProcessor = isApproved && broadcast.selected_claim_id === claim.id;

        return (
            <div
                key={claim.id}
                className="p-3 border-bottom-1 surface-border"
                style={isCurrentProcessor ? { backgroundColor: 'var(--green-50, #ecfdf5)' } : {}}
            >
                <div className="flex flex-wrap align-items-start justify-content-between gap-2">
                    <div className="flex align-items-start gap-2 flex-grow-1">
                        <i className={`pi ${isCurrentProcessor ? 'pi-star-fill text-green-500' : 'pi-send'} text-lg mt-1`} style={{ minWidth: '1.25rem' }} />
                        <div>
                            <div className="flex align-items-center gap-2 flex-wrap">
                                <span className="font-semibold text-900">Your Claim</span>
                                {renderClaimStatusTag(claim.status)}
                                {isCurrentProcessor && <Tag value="You are Processor" severity="success" className="text-xs" />}
                            </div>
                            <div className="text-500 text-xs mt-1">
                                Claimed {claim.created_at || 'recently'}
                                {claim.responded_at && (
                                    <span>
                                        {' '} · {claim.status === 'approved' ? 'Approved' : claim.status === 'rejected' ? 'Rejected' : 'Responded'} {claim.responded_at}
                                        {claim.responded_by?.name && ` by ${claim.responded_by.name}`}
                                    </span>
                                )}
                            </div>
                            {claim.status === 'rejected' && claim.rejected_reason && <div className="text-red-600 text-xs mt-1">Reason: {claim.rejected_reason}</div>}
                        </div>
                    </div>
                    {isPending && (
                        <div className="flex gap-2 align-items-center flex-shrink-0">
                            <Button label="Withdraw" icon="pi pi-undo" className="p-button-outlined p-button-secondary p-button-sm" onClick={() => openWithdrawDialog(claim, offer)} />
                        </div>
                    )}
                </div>
                {/* Your claim message — prominent bubble */}
                {claim.claim_message && (
                    <div className="mt-2 ml-5 p-2 border-round surface-100" style={{ borderLeft: '3px solid var(--teal-500)' }}>
                        <div className="flex align-items-center gap-1 mb-1">
                            <i className="pi pi-comment text-teal-600 text-xs" />
                            <span className="text-teal-600 text-xs font-semibold">Your Message</span>
                        </div>
                        <div className="text-700 text-sm">"{claim.claim_message}"</div>
                    </div>
                )}
            </div>
        );
    };

    // ══════════════════════════════════════════

    // ══════════════════════════════════════════
    // OFFERS SLIDER (shared by both tabs)
    // ══════════════════════════════════════════

    const renderOffersSlider = (offerGroups, broadcast, offerIdxState, setOfferIdxState) => {
        const totalOffers = offerGroups.length;
        if (totalOffers === 0) return <Message severity="info" text="No claims match the current filter." className="w-full" />;

        // Extract offers array for OffersComparison
        const offers = offerGroups.map(og => og.offer).filter(Boolean);
        // Build a lookup map: offer_id → offerGroup (for claims)
        const offerGroupMap = {};
        offerGroups.forEach(og => {
            const oid = og.offer?.offer_id || og.offer?.id;
            if (oid) offerGroupMap[oid] = og;
        });

        const mode = broadcast._mode;
        const renderClaimFn = mode === 'received' ? renderReceivedClaimItem : renderSentClaimItem;

        // Header: rejection details + status badge at top of each offer
        const offerHeaderRenderer = (offer) => {
            const offerId = offer.offer_id || offer.id;
            const offerStatusData = broadcast.offer_statuses?.[offerId];
            const offerProcessingStatus = offerStatusData?.status || 'available';

            if (!offerStatusData || offerProcessingStatus === 'available') return null;

            return (
                <div className="mb-1">
                    <OfferStatusBadge offerStatus={offerStatusData} />
                </div>
            );
        };

        // Footer: claims section per offer
        const offerFooterRenderer = (offer) => {
            const offerId = offer.offer_id || offer.id;
            const og = offerGroupMap[offerId];
            if (!og) return null;

            const { claims, total_claims } = og;
            const claimsSectionLabel = mode === 'received' ? `CLAIMS (${total_claims})` : `YOUR CLAIM${total_claims > 1 ? 'S' : ''} (${total_claims})`;

            return (
                <>
                    <Divider className="my-2" />
                    <div className="flex align-items-center gap-2 mb-2 p-2 border-round" style={{ backgroundColor: 'var(--yellow-50)' }}>
                        <i className={`pi ${mode === 'received' ? 'pi-users' : 'pi-send'} text-yellow-600`} />
                        <span className="font-semibold text-yellow-800 text-sm">{claimsSectionLabel}</span>
                    </div>
                    <div className="border-1 surface-border border-round">
                        {claims.map((claim) => renderClaimFn(claim, broadcast, offer))}
                    </div>
                </>
            );
        };

        return (
            <div>
                <OffersComparison
                    offers={offers}
                    selectedOffer={broadcast.selected_offer_id ? { offer_id: broadcast.selected_offer_id } : null}
                    readOnly={true}
                    hideEligibility={false}
                    renderOfferHeader={offerHeaderRenderer}
                    renderOfferFooter={offerFooterRenderer}
                    stickyHeaderTop="4rem"
                />
            </div>
        );
    };

    // ══════════════════════════════════════════
    // COLLAPSED / EXPANDED CARDS (shared)
    // ══════════════════════════════════════════

    const renderCollapsedCard = (broadcast, toggleFn) => {
        const cs = broadcast.claims_summary || {};
        const ls = broadcast.lead_summary;
        const isSent = broadcast._mode === 'sent';

        // For Sent tab: collect offer summaries to show overview without expanding
        const offerGroups = broadcast.claims_by_offer || [];
        const offerPreviews = isSent
            ? offerGroups.map(og => og.offer).filter(Boolean).slice(0, 3)
            : [];

        // Determine the single most important attention indicator
        const attentionIndicator = cs.pending > 0
            ? { count: cs.pending, label: 'pending', color: 'var(--yellow-600)', bg: 'var(--yellow-50)' }
            : cs.rejected > 0
            ? { count: cs.rejected, label: 'rejected', color: 'var(--red-600)', bg: 'var(--red-50)' }
            : cs.approved > 0
            ? { count: cs.approved, label: 'approved', color: 'var(--green-600)', bg: 'var(--green-50)' }
            : null;

        // Received tab: grid-aligned compact row
        if (!isSent) {
            const pConfig = broadcast.lead_status
                ? getProcessingStatusConfig(broadcast.lead_status) : null;

            // Grid style shared between header and rows — fixed column widths for alignment
            const gridStyle = {
                display: 'grid',
                gridTemplateColumns: '1fr 6.5rem 5rem 3rem 6.5rem 8.5rem 6rem 1.2rem',
                alignItems: 'center',
                gap: '0 0.5rem',
            };
            const mobileGridStyle = {
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                gap: '0.25rem 0.5rem',
            };

            return (
                <div
                    className="cursor-pointer hover:surface-hover transition-colors transition-duration-150 border-bottom-1 surface-border"
                    onClick={() => toggleFn(broadcast.broadcast_id)}
                >
                    {/* Desktop row */}
                    <div className="hidden md:block">
                    <div className="px-3 py-2" style={gridStyle}>
                        {/* Lead */}
                        <div className="flex align-items-center gap-2" style={{ minWidth: 0 }}>
                            <span className="font-semibold text-900 text-sm white-space-nowrap">{broadcast.lead_id_formatted}</span>
                            {ls?.name && ls.name.trim() && (
                                <span className="text-700 text-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ls.name}</span>
                            )}
                        </div>
                        {/* Product */}
                        <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                            <span className="text-xs font-medium text-700 white-space-nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {formatProductType(ls?.product_type_detailed || ls?.product_type)}
                            </span>
                        </div>
                        {/* Amount */}
                        <span className="text-sm font-semibold text-blue-600 text-right">{ls?.loan_amount ? formatLoanAmount(ls.loan_amount) : '—'}</span>
                        {/* Offers */}
                        <span className="text-xs text-500 text-center">{broadcast.total_offers || '—'}</span>
                        {/* Processor */}
                        <div className="flex align-items-center gap-1 justify-content-center" style={{ overflow: 'hidden' }}>
                            {broadcast.selected_processor ? (
                                <span className="text-xs text-700" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <i className="pi pi-user text-xs text-400 mr-1" />{broadcast.selected_processor}
                                </span>
                            ) : <span className="text-xs text-300">—</span>}
                        </div>
                        {/* Status */}
                        <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                            {pConfig ? (
                                <Tag value={pConfig.label} severity={pConfig.severity} icon={`pi ${pConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', maxWidth: '100%', whiteSpace: 'nowrap' }} />
                            ) : <span className="text-xs text-300">—</span>}
                        </div>
                        {/* Attention */}
                        <div className="flex justify-content-end">
                            {attentionIndicator ? (
                                <span className="text-xs font-semibold px-2 py-1 border-round white-space-nowrap" style={{ color: attentionIndicator.color, backgroundColor: attentionIndicator.bg }}>
                                    {attentionIndicator.count} {attentionIndicator.label}
                                </span>
                            ) : <span className="text-xs text-300">—</span>}
                        </div>
                        {/* Chevron */}
                        <i className="pi pi-chevron-down text-400 text-xs text-right" />
                    </div>
                    </div>

                    {/* Mobile row */}
                    <div className="block md:hidden px-3 py-2" style={mobileGridStyle}>
                        <div style={{ minWidth: 0 }}>
                            <div className="flex align-items-center gap-2">
                                <span className="font-semibold text-900 text-sm">{broadcast.lead_id_formatted}</span>
                                <span className="text-xs text-600 font-medium">{formatProductType(ls?.product_type_detailed || ls?.product_type)}</span>
                            </div>
                            <div className="flex align-items-center gap-2 mt-1">
                                {ls?.loan_amount && <span className="text-xs font-semibold text-blue-600">{formatLoanAmount(ls.loan_amount)}</span>}
                                <span className="text-xs text-500">{broadcast.total_offers} offers</span>
                                {broadcast.selected_processor && (
                                    <span className="text-xs text-600"><i className="pi pi-user text-xs mr-1" />{broadcast.selected_processor}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-column align-items-end gap-1">
                            {pConfig && (
                                <Tag value={pConfig.label} severity={pConfig.severity} icon={`pi ${pConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.1rem 0.3rem' }} />
                            )}
                            {attentionIndicator && (
                                <span className="text-xs font-semibold px-2 py-1 border-round" style={{ color: attentionIndicator.color, backgroundColor: attentionIndicator.bg }}>
                                    {attentionIndicator.count} {attentionIndicator.label}
                                </span>
                            )}
                            <i className="pi pi-chevron-down text-400 text-xs" />
                        </div>
                    </div>
                </div>
            );
        }

        // Sent tab: same compact grid layout for consistency
        const sentPConfig = broadcast.lead_status
            ? getProcessingStatusConfig(broadcast.lead_status) : null;

        const sentGridStyle = {
            display: 'grid',
            gridTemplateColumns: '1fr 6.5rem 5rem 3rem 6.5rem 8.5rem 6rem 1.2rem',
            alignItems: 'center',
            gap: '0 0.5rem',
        };
        const sentMobileGridStyle = {
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '0.25rem 0.5rem',
        };

        return (
            <div
                className="cursor-pointer hover:surface-hover transition-colors transition-duration-150 border-bottom-1 surface-border"
                onClick={() => toggleFn(broadcast.broadcast_id)}
            >
                {/* Desktop row */}
                <div className="hidden md:block">
                <div className="px-3 py-2" style={sentGridStyle}>
                    {/* Lead */}
                    <div className="flex align-items-center gap-2" style={{ minWidth: 0 }}>
                        <span className="font-semibold text-900 text-sm white-space-nowrap">{broadcast.lead_id_formatted}</span>
                        {ls?.name && ls.name.trim() && (
                            <span className="text-700 text-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ls.name}</span>
                        )}
                    </div>
                    {/* Product */}
                    <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                        <span className="text-xs font-medium text-700 white-space-nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {formatProductType(ls?.product_type_detailed || ls?.product_type)}
                        </span>
                    </div>
                    {/* Amount */}
                    <span className="text-sm font-semibold text-blue-600 text-right">{ls?.loan_amount ? formatLoanAmount(ls.loan_amount) : '—'}</span>
                    {/* Offers */}
                    <span className="text-xs text-500 text-center">{broadcast.total_offers || '—'}</span>
                    {/* Broadcast By */}
                    <div className="flex align-items-center gap-1 justify-content-center" style={{ overflow: 'hidden' }}>
                        {broadcast.broadcast_by ? (
                            <span className="text-xs text-700" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <i className="pi pi-megaphone text-xs text-400 mr-1" />{broadcast.broadcast_by}
                            </span>
                        ) : <span className="text-xs text-300">—</span>}
                    </div>
                    {/* Status */}
                    <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                        {sentPConfig ? (
                            <Tag value={sentPConfig.label} severity={sentPConfig.severity} icon={`pi ${sentPConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', maxWidth: '100%', whiteSpace: 'nowrap' }} />
                        ) : <span className="text-xs text-300">—</span>}
                    </div>
                    {/* Attention */}
                    <div className="flex justify-content-end">
                        {attentionIndicator ? (
                            <span className="text-xs font-semibold px-2 py-1 border-round white-space-nowrap" style={{ color: attentionIndicator.color, backgroundColor: attentionIndicator.bg }}>
                                {attentionIndicator.count} {attentionIndicator.label}
                            </span>
                        ) : <span className="text-xs text-300">—</span>}
                    </div>
                    {/* Chevron */}
                    <i className="pi pi-chevron-down text-400 text-xs text-right" />
                </div>
                </div>

                {/* Mobile row */}
                <div className="block md:hidden px-3 py-2" style={sentMobileGridStyle}>
                    <div style={{ minWidth: 0 }}>
                        <div className="flex align-items-center gap-2">
                            <span className="font-semibold text-900 text-sm">{broadcast.lead_id_formatted}</span>
                            <span className="text-xs text-600 font-medium">{formatProductType(ls?.product_type_detailed || ls?.product_type)}</span>
                        </div>
                        <div className="flex align-items-center gap-2 mt-1">
                            {ls?.loan_amount && <span className="text-xs font-semibold text-blue-600">{formatLoanAmount(ls.loan_amount)}</span>}
                            <span className="text-xs text-500">{broadcast.total_offers} offers</span>
                            {broadcast.broadcast_by && (
                                <span className="text-xs text-600">by {broadcast.broadcast_by}</span>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-column align-items-end gap-1">
                        {sentPConfig && (
                            <Tag value={sentPConfig.label} severity={sentPConfig.severity} icon={`pi ${sentPConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.1rem 0.3rem' }} />
                        )}
                        {attentionIndicator && (
                            <span className="text-xs font-semibold px-2 py-1 border-round" style={{ color: attentionIndicator.color, backgroundColor: attentionIndicator.bg }}>
                                {attentionIndicator.count} {attentionIndicator.label}
                            </span>
                        )}
                        <i className="pi pi-chevron-down text-400 text-xs" />
                    </div>
                </div>
            </div>
        );
    };

    const renderExpandedCard = (broadcast, toggleFn, offerIdxState, setOfferIdxState) => {
        const cs = broadcast.claims_summary || {};
        const ls = broadcast.lead_summary;
        const isSent = broadcast._mode === 'sent';
        const isProcessorSelected = broadcast.broadcast_status === 'processor_selected';

        // Status display — uses lead_status (single source of truth)
        const pConfig = broadcast.lead_status
            ? getProcessingStatusConfig(broadcast.lead_status) : null;

        return (
            <div
                className="surface-card border-round shadow-1 mb-2 overflow-hidden"
                style={{ borderLeft: `3px solid var(--${isSent ? 'teal' : 'blue'}-500)` }}
            >
                {/* Compact Header — mirrors collapsed row, click to collapse */}
                <div
                    className="cursor-pointer hover:surface-hover transition-colors transition-duration-150 border-bottom-1 surface-border"
                    onClick={() => toggleFn(broadcast.broadcast_id)}
                >
                    {/* Desktop header — same 7-column grid as collapsed row for alignment */}
                    <div className="hidden md:block">
                        <div className="px-3 py-2" style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 6.5rem 5rem 3rem 6.5rem 8.5rem 6rem 1.2rem',
                            alignItems: 'center',
                            gap: '0 0.5rem',
                        }}>
                            {/* LEAD — name only */}
                            <div className="flex align-items-center gap-2" style={{ minWidth: 0 }}>
                                <span className="font-semibold text-900 text-sm white-space-nowrap">{broadcast.lead_id_formatted}</span>
                                {ls?.name && ls.name.trim() && (
                                    <span className="text-700 text-sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ls.name}</span>
                                )}
                            </div>
                            {/* PRODUCT */}
                            <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                                <span className="text-xs font-medium text-700 white-space-nowrap" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {formatProductType(ls?.product_type_detailed || ls?.product_type)}
                                </span>
                            </div>
                            {/* AMOUNT */}
                            <span className="text-sm font-semibold text-blue-600 text-right">{ls?.loan_amount ? formatLoanAmount(ls.loan_amount) : '—'}</span>
                            {/* OFFERS */}
                            <span className="text-xs text-500 text-center">{broadcast.total_offers || '—'}</span>
                            {/* BY / PROCESSOR */}
                            {!isSent ? (
                                <div className="flex align-items-center gap-1 justify-content-center" style={{ overflow: 'hidden' }}>
                                    {broadcast.selected_processor ? (
                                        <span className="text-xs text-700" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <i className="pi pi-user text-xs text-400 mr-1" />{broadcast.selected_processor}
                                        </span>
                                    ) : <span className="text-xs text-300">—</span>}
                                </div>
                            ) : (
                                <div className="flex align-items-center justify-content-center" style={{ overflow: 'hidden' }}>
                                    <span className="text-xs text-700" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {broadcast.broadcast_by || '—'}
                                    </span>
                                </div>
                            )}
                            {/* STATUS */}
                            <div className="flex justify-content-center" style={{ overflow: 'hidden' }}>
                                {pConfig ? (
                                    <Tag value={pConfig.label} severity={pConfig.severity} icon={`pi ${pConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', maxWidth: '100%', whiteSpace: 'nowrap' }} />
                                ) : <span className="text-xs text-300">—</span>}
                            </div>
                            {/* CLAIMS — attention indicator, same as collapsed row */}
                            <div className="flex justify-content-end">
                                {cs.pending > 0 ? (
                                    <span className="text-xs font-semibold px-2 py-1 border-round white-space-nowrap" style={{ color: 'var(--yellow-600)', backgroundColor: 'var(--yellow-50)' }}>{cs.pending} pending</span>
                                ) : cs.approved > 0 ? (
                                    <span className="text-xs font-semibold px-2 py-1 border-round white-space-nowrap" style={{ color: 'var(--green-600)', backgroundColor: 'var(--green-50)' }}>{cs.approved} approved</span>
                                ) : <span className="text-xs text-300">—</span>}
                            </div>
                            {/* CHEVRON */}
                            <i className="pi pi-chevron-up text-400 text-xs text-right" />
                        </div>
                    </div>
                    {/* Mobile header */}
                    <div className="block md:hidden px-3 py-3" style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.25rem 0.5rem' }}>
                        <div style={{ minWidth: 0 }}>
                            <div className="flex align-items-center gap-2 mb-1">
                                <span className="font-semibold text-900" style={{ fontSize: '0.85rem' }}>{broadcast.lead_id_formatted}</span>
                                {ls?.name && ls.name.trim() && <span className="text-700" style={{ fontSize: '0.8rem' }}>{ls.name}</span>}
                            </div>
                            <div className="flex align-items-center gap-2 flex-wrap">
                                {ls?.loan_amount && <span className="font-semibold text-blue-600" style={{ fontSize: '0.8rem' }}>{formatLoanAmount(ls.loan_amount)}</span>}
                                <span className="text-500" style={{ fontSize: '0.75rem' }}>{broadcast.total_offers} offers</span>
                                {broadcast.selected_processor && (
                                    <span className="text-600" style={{ fontSize: '0.75rem' }}><i className="pi pi-user text-400 mr-1" style={{ fontSize: '0.65rem' }} />{broadcast.selected_processor}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-column align-items-end gap-1 pl-2">
                            {pConfig && <Tag value={pConfig.label} severity={pConfig.severity} icon={`pi ${pConfig.icon}`} className="text-xs" style={{ fontSize: '0.65rem', padding: '0.15rem 0.35rem' }} />}
                            <i className="pi pi-chevron-up text-400" style={{ fontSize: '0.7rem' }} />
                        </div>
                    </div>
                </div>

                {/* Expanded Content */}
                <div className="px-3 py-2">
                    {/* Lead summary — compact */}
                    {ls && (
                        <div className="surface-50 border-round p-2 mb-2">
                            {/* Personal Information */}
                            <div className="flex align-items-center gap-1 mb-1">
                                <i className="pi pi-user text-primary" style={{ fontSize: '0.75rem' }} />
                                <span className="font-semibold text-primary" style={{ fontSize: '0.7rem' }}>PERSONAL</span>
                                {ls.is_masked && <Tag value="Masked" severity="warning" style={{ fontSize: '0.55rem', padding: '0.05rem 0.2rem' }} />}
                            </div>
                            <div className="grid">
                                {ls.name && ls.name.trim() && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Full Name</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.name}</div>
                                    </div>
                                )}
                                {ls.phone && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Mobile</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.phone}</div>
                                    </div>
                                )}
                                {ls.email && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Email</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.email}</div>
                                    </div>
                                )}
                                {ls.pan && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>PAN</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.pan}</div>
                                    </div>
                                )}
                            </div>

                            {/* Professional & Financial */}
                            <div className="flex align-items-center gap-1 mt-1 mb-1 pt-1 border-top-1 surface-border">
                                <i className="pi pi-briefcase text-purple-500" style={{ fontSize: '0.75rem' }} />
                                <span className="font-semibold text-purple-700" style={{ fontSize: '0.7rem' }}>PROFESSIONAL & FINANCIAL</span>
                            </div>
                            <div className="grid">
                                <div className="col-6 md:col-3 mb-1">
                                    <div className="text-500" style={{ fontSize: '0.65rem' }}>Product Type</div>
                                    <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{formatProductType(ls.product_type_detailed || ls.product_type)}</div>
                                </div>
                                {ls.employment_type && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Employment</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.employment_type?.replace('_', ' ').toUpperCase()}</div>
                                    </div>
                                )}
                                {ls.loan_amount && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Loan Amount</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{formatCurrency(ls.loan_amount)}</div>
                                    </div>
                                )}
                                {ls.lead_status && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Status</div>
                                                {(() => { const c = getProcessingStatusConfig(ls.lead_status); return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} style={{ fontSize: '0.6rem', padding: '0.05rem 0.25rem' }} />; })()}
                                    </div>
                                )}
                                {ls.company_name && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Company</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.company_name}</div>
                                    </div>
                                )}
                                {ls.net_salary && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Net Salary</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{formatCurrency(ls.net_salary)}</div>
                                    </div>
                                )}
                                {ls.business_type && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Business Type</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.business_type}</div>
                                    </div>
                                )}
                                {ls.annual_turnover && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Annual Turnover</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{formatCurrency(ls.annual_turnover)}</div>
                                    </div>
                                )}
                                {ls.profession_type && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Profession</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.profession_type}</div>
                                    </div>
                                )}
                                {ls.gross_receipts && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>Gross Receipts</div>
                                        <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{formatCurrency(ls.gross_receipts)}</div>
                                    </div>
                                )}
                                {ls.cibil_score && (
                                    <div className="col-6 md:col-3 mb-1">
                                        <div className="text-500" style={{ fontSize: '0.65rem' }}>CIBIL Score</div>
                                        <div className={`font-semibold ${ls.cibil_score >= 750 ? 'text-green-600' : ls.cibil_score >= 650 ? 'text-yellow-600' : 'text-red-600'}`} style={{ fontSize: '0.8rem' }}>{ls.cibil_score}</div>
                                    </div>
                                )}
                            </div>

                            {/* Location */}
                            {(ls.city || ls.state || ls.pincode || ls.residency) && (
                                <>
                                    <div className="flex align-items-center gap-1 mt-1 mb-1 pt-1 border-top-1 surface-border">
                                        <i className="pi pi-map-marker text-primary" style={{ fontSize: '0.75rem' }} />
                                        <span className="font-semibold text-primary" style={{ fontSize: '0.7rem' }}>LOCATION</span>
                                    </div>
                                    <div className="grid">
                                        {ls.city && (
                                            <div className="col-6 md:col-3 mb-1">
                                                <div className="text-500" style={{ fontSize: '0.65rem' }}>City</div>
                                                <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.city}</div>
                                            </div>
                                        )}
                                        {ls.state && (
                                            <div className="col-6 md:col-3 mb-1">
                                                <div className="text-500" style={{ fontSize: '0.65rem' }}>State</div>
                                                <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.state}</div>
                                            </div>
                                        )}
                                        {ls.pincode && (
                                            <div className="col-6 md:col-3 mb-1">
                                                <div className="text-500" style={{ fontSize: '0.65rem' }}>Pincode</div>
                                                <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.pincode}</div>
                                            </div>
                                        )}
                                        {ls.residency && (
                                            <div className="col-6 md:col-3 mb-1">
                                                <div className="text-500" style={{ fontSize: '0.65rem' }}>Residency</div>
                                                <div className="text-900 font-semibold" style={{ fontSize: '0.8rem' }}>{ls.residency}</div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Processor Action Bar — Sent tab only, only for the assigned processor */}
                    {isSent && broadcast.broadcast_status === 'processor_selected' && (() => {
                        // Check if current user is the approved processor (has approved claim in sent tab)
                        const myApprovedClaim = (broadcast.claims_by_offer || []).flatMap(og => og.claims || []).find(c => c.status === 'approved');
                        if (!myApprovedClaim) return null; // Not the processor — don't show action bar

                        const hasApplication = !!broadcast.active_application;
                        const leadId = broadcast.lead_id || broadcast.broadcast_lead_id;
                        const currentLeadStatus = broadcast.lead_status || broadcast.lead_summary?.lead_status;

                        // Extract bank info from the selected offer
                        const offers = broadcast.broadcast_offers?.offers || broadcast.offers || (broadcast.claims_by_offer || []).map(og => og.offer).filter(Boolean) || [];
                        const selectedOffer = broadcast.selected_offer_id ? offers.find(o => (o.offer_id || o.id) === broadcast.selected_offer_id) : null;
                        const currentBankId = selectedOffer?.bank_id || selectedOffer?.lender_id || null;
                        const currentBankName = selectedOffer?.bank_name || selectedOffer?.lender_name || broadcast.lead_summary?.processing_bank_name || null;
                        const banks = [...new Map(offers.filter(o => o.bank_name || o.lender_name).map(o => [o.bank_id || o.lender_id || o.bank_name, { id: o.bank_id || o.lender_id || null, name: o.bank_name || o.lender_name }])).values()];

                        const openStatusDialog = (preselect) => {
                            setStatusDialogLeadId(leadId);
                            setStatusDialogCurrentStatus(currentLeadStatus);
                            setStatusDialogPreselect(preselect);
                            setStatusDialogBankId(currentBankId);
                            setStatusDialogBankName(currentBankName);
                            setStatusDialogOfferBanks(banks);
                            setStatusDialogVisible(true);
                        };

                        const handleStartProcessing = async () => {
                            const offerId = broadcast.selected_offer_id;
                            if (!offerId) return;
                            setStartProcessingLoading(true);
                            try {
                                const res = await createApplication(broadcast.broadcast_id || broadcast.id, offerId);
                                if (res.success) {
                                    toast.current?.show({ severity: 'success', summary: 'Started', detail: 'Processing started', life: 3000 });
                                    loadSentClaims(Math.floor(scFirst / ITEMS_PER_PAGE) + 1);
                                } else {
                                    toast.current?.show({ severity: 'error', summary: 'Error', detail: res.message, life: 4000 });
                                }
                            } catch (e) {
                                toast.current?.show({ severity: 'error', summary: 'Error', detail: e.response?.data?.message || e.message || 'Failed', life: 4000 });
                            } finally {
                                setStartProcessingLoading(false);
                            }
                        };

                        return (
                            <div className="flex align-items-center justify-content-between px-3 py-2 mb-2 border-round" style={{ background: 'var(--blue-50)', border: '1px solid var(--blue-200)' }}>
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-user text-blue-600" style={{ fontSize: '0.85rem' }} />
                                    <span className="text-blue-700 font-semibold" style={{ fontSize: '0.8rem' }}>You are the Processor</span>
                                    {currentLeadStatus && (() => {
                                        const c = getProcessingStatusConfig(currentLeadStatus);
                                        return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} style={{ fontSize: '0.6rem', padding: '0.1rem 0.3rem' }} />;
                                    })()}
                                </div>
                                <div className="flex gap-2">
                                    {!hasApplication ? (
                                        <Button
                                            label="Start Processing"
                                            icon="pi pi-play"
                                            className="p-button-sm"
                                            onClick={handleStartProcessing}
                                            loading={startProcessingLoading}
                                            style={{ fontSize: '0.75rem' }}
                                        />
                                    ) : (
                                        <Button
                                            label="Update Status"
                                            icon="pi pi-pencil"
                                            className="p-button-sm"
                                            onClick={() => openStatusDialog(null)}
                                            style={{ fontSize: '0.75rem' }}
                                        />
                                    )}
                                    <Button
                                        label="Can't Process"
                                        icon="pi pi-times"
                                        className="p-button-sm p-button-outlined p-button-danger"
                                        onClick={() => openStatusDialog('processor_rejected')}
                                        style={{ fontSize: '0.75rem' }}
                                    />
                                </div>
                            </div>
                        );
                    })()}

                    {/* Processing Status Banner — Received tab only, compact */}
                    {!isSent && broadcast.processing_status && broadcast.processing_status !== 'not_started' && (
                        <div className={`px-2 py-2 mb-2 border-round ${
                            broadcast.processing_status === 'retry_needed' ? 'border-1 border-orange-300' :
                            broadcast.processing_status === 'exhausted' ? 'border-1 border-red-300' :
                            broadcast.processing_status === 'approved' || broadcast.processing_status === 'disbursed' ? 'border-1 border-green-300' :
                            ''
                        }`} style={{
                            backgroundColor: broadcast.processing_status === 'retry_needed' ? 'var(--orange-50)' :
                                broadcast.processing_status === 'exhausted' ? 'var(--red-50)' :
                                broadcast.processing_status === 'approved' || broadcast.processing_status === 'disbursed' ? 'var(--green-50)' :
                                'var(--surface-50)'
                        }}>
                            {broadcast.processing_status === 'retry_needed' ? (
                                <>
                                    {(() => {
                                        const rejectedOffers = broadcast.offer_statuses ? Object.entries(broadcast.offer_statuses).filter(([, s]) => ['rejected', 'bank_rejected', 'customer_rejected', 'processor_rejected'].includes(s.status)) : [];
                                        const lastRejection = rejectedOffers.length > 0 ? rejectedOffers[rejectedOffers.length - 1][1] : null;
                                        const isProcessorRejection = lastRejection?.rejection_type === 'processor';
                                        return (
                                            <>
                                                <div className="flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                                                    <div className="flex align-items-center gap-2 flex-wrap">
                                                        <Tag
                                                            value={isProcessorRejection ? 'Processor Rejected' : 'Bank Rejected'}
                                                            severity={isProcessorRejection ? 'warning' : 'danger'}
                                                            icon={`pi ${isProcessorRejection ? 'pi-user-minus' : 'pi-times-circle'}`}
                                                            style={{ fontSize: '0.7rem' }}
                                                        />
                                                        {broadcast.selected_processor && <span className="text-700 text-xs">Processor: <strong>{broadcast.selected_processor}</strong></span>}
                                                    </div>
                                                    <Button label="Close Lead" icon="pi pi-times" className="p-button-danger p-button-outlined" onClick={() => openDecisionDialog(broadcast, 'close')} style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }} />
                                                </div>
                                                {rejectedOffers.length > 0 && (
                                                    <div className="mb-2">
                                                        {rejectedOffers.map(([offerId, s]) => {
                                                            const isProcReject = s.rejection_type === 'processor';
                                                            return (
                                                                <div key={offerId} className="flex align-items-start gap-2 px-2 py-1 border-round mb-1" style={{ backgroundColor: isProcReject ? 'var(--orange-50)' : 'var(--red-50)' }}>
                                                                    <i className={`pi ${isProcReject ? 'pi-user-minus text-orange-500' : 'pi-lock text-red-500'}`} style={{ fontSize: '0.75rem', marginTop: '2px' }} />
                                                                    <div className="flex-grow-1">
                                                                        <div className="flex align-items-center gap-1 flex-wrap">
                                                                            <Tag value={isProcReject ? 'Processor' : 'Bank'} severity={isProcReject ? 'warning' : 'danger'} style={{ fontSize: '0.55rem', padding: '0.05rem 0.2rem' }} />
                                                                            <span className={`${isProcReject ? 'text-orange-700' : 'text-red-700'} font-semibold`} style={{ fontSize: '0.75rem' }}>{s.rejection_reason_label || getReasonLabel(s.rejection_reason_code)}</span>
                                                                            {s.rejected_by_name && <span className="text-500" style={{ fontSize: '0.65rem' }}>· by <strong>{s.rejected_by_name}</strong></span>}
                                                                        </div>
                                                                        {s.rejection_detail && <div className={`${isProcReject ? 'text-orange-600' : 'text-red-600'} font-italic mt-1`} style={{ fontSize: '0.65rem' }}>"{s.rejection_detail}"</div>}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}
                                    <div className="flex align-items-center gap-2 px-2 py-1 border-round" style={{ backgroundColor: 'var(--blue-50)' }}>
                                        <i className="pi pi-info-circle text-blue-500" style={{ fontSize: '0.75rem' }} />
                                        <span className="text-blue-700" style={{ fontSize: '0.75rem' }}>Select another processor using <strong>"Make Processor"</strong> below</span>
                                    </div>
                                </>
                            ) : (
                                <div className="flex align-items-center gap-2">
                                    {(() => { const pc = getProcessingStatusConfig(broadcast.lead_status || broadcast.processing_status); return <Tag value={pc.label} severity={pc.severity} icon={`pi ${pc.icon}`} style={{ fontSize: '0.7rem' }} />; })()}
                                    {broadcast.selected_processor && <span className="text-700 text-xs">Processor: <strong>{broadcast.selected_processor}</strong></span>}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Regeneration info — compact inline */}
                    {broadcast.broadcast_offers?.regeneration_history?.length > 1 && (
                        <div className="flex align-items-center gap-2 px-2 py-1 mb-2 border-round" style={{ backgroundColor: 'var(--yellow-50)' }}>
                            <i className="pi pi-refresh text-yellow-600" style={{ fontSize: '0.75rem' }} />
                            <span className="text-xs text-700">
                                Updated {broadcast.broadcast_offers.regeneration_history.length - 1}x — {broadcast.broadcast_offers.regeneration_history[broadcast.broadcast_offers.regeneration_history.length - 1]?.generated_by_name},
                                {' '}{new Date(broadcast.broadcast_offers.regeneration_history[broadcast.broadcast_offers.regeneration_history.length - 1]?.generated_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </span>
                        </div>
                    )}

                    {/* Offer cards with claims */}
                    {renderOffersSlider(broadcast.claims_by_offer || [], broadcast, offerIdxState, setOfferIdxState)}
                </div>

                {/* ProcessingWorkflowPanel removed — replaced by Processor Action Bar at top */}

                {/* Sent tab: Processing History */}
                {isSent && broadcast.processing_status && broadcast.processing_status !== 'not_started' && (
                    <div className="px-3 pb-3">
                        {!processingHistoryMap[broadcast.broadcast_id] ? (
                            <Button label="Show Processing History" icon="pi pi-history" className="p-button-text p-button-sm" onClick={() => loadProcessingHistory(broadcast.broadcast_id)} />
                        ) : (
                            <ProcessingHistoryTimeline applications={processingHistoryMap[broadcast.broadcast_id]?.applications || []} />
                        )}
                    </div>
                )}

                {/* Received tab: Processing History — load on expand */}
                {!isSent && broadcast.processing_status && broadcast.processing_status !== 'not_started' && (
                    <div className="px-3 pb-3">
                        {!processingHistoryMap[broadcast.broadcast_id] ? (
                            <Button label="Show Processing History" icon="pi pi-history" className="p-button-text p-button-sm" onClick={() => loadProcessingHistory(broadcast.broadcast_id)} />
                        ) : (
                            <ProcessingHistoryTimeline applications={processingHistoryMap[broadcast.broadcast_id]?.applications || []} />
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ══════════════════════════════════════════
    // TAB CONTENT RENDERERS
    // ══════════════════════════════════════════

    const renderTabContent = ({
        data, loading, summary, filter, setFilter, loanTypeFilter, setLoanTypeFilter, first, setFirst, total, loadFn,
        expandedState, setExpandedState, offerIdxState, setOfferIdxState,
        searchInput, setSearchInput, searchValue, setSearchValue,
        dateFrom, setDateFrom, dateTo, setDateTo,
        mode, emptyIcon, emptyTitle, emptyMsg,
    }) => {
        const toggleExpand = (broadcastId) => setExpandedState(prev => ({ ...prev, [broadcastId]: !prev[broadcastId] }));

        // Tag each broadcast with _mode so shared render functions know the context
        const taggedData = data.map(b => ({ ...b, _mode: mode }));

        // Pagination info
        const currentPage = Math.floor(first / ITEMS_PER_PAGE) + 1;

        const hasActiveFilters = filter !== 'all' || searchValue || dateFrom || dateTo || loanTypeFilter;

        const handleSearchSubmit = () => {
            setSearchValue(searchInput.trim());
            setFirst(0);
            setExpandedState({});
        };

        const handleClearFilters = () => {
            setFilter('all');
            setLoanTypeFilter(null);
            setSearchInput('');
            setSearchValue('');
            setDateFrom(null);
            setDateTo(null);
            setFirst(0);
            setExpandedState({});
        };

        return (
            <>
                {/* Filters Row - Compact (matching MyOffers/MyPayouts pattern) */}
                <div className="surface-card border-round shadow-1 p-2 mb-2">
                    <div className="flex align-items-end gap-2 flex-wrap">
                        {/* Search */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '180px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Search</label>
                            <span className="p-input-icon-left w-full">
                                <i className="pi pi-search" style={{ fontSize: '0.7rem' }} />
                                <InputText
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                                    placeholder="Lead ID or name..."
                                    className="w-full p-inputtext-sm"
                                    style={{ fontSize: '0.7rem', paddingLeft: '2rem' }}
                                />
                            </span>
                        </div>

                        {/* Status */}
                        <div className="flex-1" style={{ minWidth: '100px', maxWidth: '130px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Status</label>
                            <Dropdown
                                value={filter}
                                options={STATUS_FILTER_OPTIONS}
                                onChange={(e) => { setFilter(e.value); setFirst(0); setExpandedState({}); }}
                                showClear={filter !== 'all'}
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>

                        {/* Product */}
                        <div className="flex-1" style={{ minWidth: '100px', maxWidth: '150px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>Product</label>
                            <Dropdown
                                value={loanTypeFilter}
                                options={loanTypes.map(lt => ({ label: lt.name, value: lt.id }))}
                                onChange={(e) => { setLoanTypeFilter(e.value); setFirst(0); setExpandedState({}); }}
                                placeholder="All Products"
                                showClear={!!loanTypeFilter}
                                className="w-full p-inputtext-sm"
                                panelStyle={{ fontSize: '0.7rem' }}
                                pt={{ input: { style: { fontSize: '0.7rem' } }, item: { style: { fontSize: '0.7rem' } } }}
                            />
                        </div>

                        {/* From Date */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>From Date</label>
                            <Calendar
                                value={dateFrom}
                                onChange={(e) => { setDateFrom(e.value); setFirst(0); setExpandedState({}); }}
                                placeholder="From"
                                className="w-full p-inputtext-sm"
                                showIcon
                                inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy"
                                showButtonBar
                                maxDate={dateTo || new Date()}
                                pt={{
                                    input: { style: { fontSize: '0.7rem' } },
                                    dropdownButton: { style: { width: '2rem', padding: '0' } },
                                    icon: { style: { fontSize: '0.75rem' } }
                                }}
                            />
                        </div>

                        {/* To Date */}
                        <div className="flex-1" style={{ minWidth: '120px', maxWidth: '160px' }}>
                            <label className="block text-500 mb-1" style={{ fontSize: '0.65rem' }}>To Date</label>
                            <Calendar
                                value={dateTo}
                                onChange={(e) => { setDateTo(e.value); setFirst(0); setExpandedState({}); }}
                                placeholder="To"
                                className="w-full p-inputtext-sm"
                                showIcon
                                inputStyle={{ fontSize: '0.7rem' }}
                                dateFormat="dd M yy"
                                showButtonBar
                                minDate={dateFrom || undefined}
                                maxDate={new Date()}
                                pt={{
                                    input: { style: { fontSize: '0.7rem' } },
                                    dropdownButton: { style: { width: '2rem', padding: '0' } },
                                    icon: { style: { fontSize: '0.75rem' } }
                                }}
                            />
                        </div>

                        {/* Refresh - Right aligned */}
                        <div className="flex align-items-center gap-1 ml-auto flex-shrink-0">
                            <Button
                                icon="pi pi-refresh"
                                className="p-button-text p-button-rounded"
                                onClick={() => loadFn(currentPage)}
                                loading={loading}
                                tooltip="Refresh"
                                tooltipOptions={{ position: 'top' }}
                                style={{ width: '1.75rem', height: '1.75rem' }}
                            />
                        </div>
                    </div>

                    {/* Active filters & Results count */}
                    <div className="flex align-items-center justify-content-between mt-2 pt-2 border-top-1 surface-border">
                        <span className="text-500" style={{ fontSize: '0.7rem' }}>
                            {loading ? 'Loading...' : `${total} lead${total !== 1 ? 's' : ''}`}
                            {hasActiveFilters && ` \u2022 filters active`}
                        </span>
                        {hasActiveFilters && (
                            <Button
                                label="Clear"
                                icon="pi pi-filter-slash"
                                className="p-button-text p-button-sm"
                                onClick={handleClearFilters}
                                style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }}
                            />
                        )}
                    </div>
                </div>

                {/* Result count — only show when filters active and no results */}
                {!loading && total === 0 && hasActiveFilters && (
                    <div className="text-500 text-sm mb-2">No results matching current filters</div>
                )}

                {/* Summary stats */}
                {!loading && summary.total > 0 && renderSummaryStats(summary)}

                {/* Content */}
                {loading ? renderSkeleton() : taggedData.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center py-4">
                        <i className={`pi ${emptyIcon} text-300 mb-2`} style={{ fontSize: '1.5rem' }} />
                        <div className="text-600 font-medium mb-1" style={{ fontSize: '0.85rem' }}>{emptyTitle}</div>
                        <div className="text-400 text-center" style={{ maxWidth: '20rem', fontSize: '0.7rem' }}>
                            {hasActiveFilters ? 'No claims found matching current filters.' : emptyMsg}
                        </div>
                        {hasActiveFilters && (
                            <Button label="Clear" icon="pi pi-filter-slash" className="p-button-text p-button-sm mt-2" onClick={handleClearFilters} style={{ fontSize: '0.65rem', padding: '0.2rem 0.4rem' }} />
                        )}
                    </div>
                ) : (
                    <>
                        {/* Column header (desktop only) */}
                        {taggedData.length > 0 && (
                            <div className="hidden md:block">
                                <div
                                    className="px-3 py-2 border-bottom-2 surface-border mb-1"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '1fr 6.5rem 5rem 3rem 6.5rem 8.5rem 6rem 1.2rem',
                                        gap: '0 0.5rem',
                                        alignItems: 'center',
                                    }}
                                >
                                    <span className="text-xs font-semibold text-500 uppercase">Lead</span>
                                    <span className="text-xs font-semibold text-500 uppercase text-center">Product</span>
                                    <span className="text-xs font-semibold text-500 uppercase text-right">Amount</span>
                                    <span className="text-xs font-semibold text-500 uppercase text-center">Offers</span>
                                    <span className="text-xs font-semibold text-500 uppercase text-center">{mode === 'received' ? 'Processor' : 'By'}</span>
                                    <span className="text-xs font-semibold text-500 uppercase text-center">Status</span>
                                    <span className="text-xs font-semibold text-500 uppercase text-right">Claims</span>
                                    <span></span>
                                </div>
                            </div>
                        )}
                        {taggedData.map((broadcast) => (
                            <div key={broadcast.broadcast_id}>
                                {expandedState[broadcast.broadcast_id]
                                    ? renderExpandedCard(broadcast, toggleExpand, offerIdxState, setOfferIdxState)
                                    : renderCollapsedCard(broadcast, toggleExpand)}
                            </div>
                        ))}
                    </>
                )}

                {/* Paginator — always visible, centered, marketplace-matching compact style */}
                <div className="flex justify-content-center mt-2">
                    <Paginator
                        first={first}
                        rows={ITEMS_PER_PAGE}
                        totalRecords={Math.max(total, 1)}
                        onPageChange={(e) => { setFirst(e.first); loadFn(Math.floor(e.first / ITEMS_PER_PAGE) + 1); }}
                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        className="surface-card border-round shadow-1"
                        style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                        pt={{
                            root: { style: { padding: '0.25rem' } },
                            firstPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            previousPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            nextPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            lastPageButton: { style: { minWidth: '1.5rem', height: '1.5rem' } },
                            pageButton: { style: { minWidth: '1.5rem', height: '1.5rem', fontSize: '0.65rem' } }
                        }}
                    />
                </div>
            </>
        );
    };

    // ══════════════════════════════════════════
    // TAB HEADERS
    // ══════════════════════════════════════════

    const receivedTabLabel = `Received (${crossTabCounts.received_total || 0})`;
    const sentTabLabel = `Sent (${crossTabCounts.sent_total || 0})`;

    // ══════════════════════════════════════════
    // DIALOG HELPERS
    // ══════════════════════════════════════════

    const isProcessorSwitching = approvingBroadcast?.broadcast_status === 'processor_selected';

    const approveDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setApproveDialogVisible(false)} disabled={approving} />
            <Button label={isProcessorSwitching ? 'Switch Processor' : 'Approve'} icon="pi pi-check" className="p-button-success" onClick={handleApprove} loading={approving} />
        </div>
    );

    const rejectDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setRejectDialogVisible(false)} disabled={rejecting} />
            <Button label="Reject" icon="pi pi-times" className="p-button-danger" onClick={handleReject} loading={rejecting} />
        </div>
    );

    const withdrawDialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setWithdrawDialogVisible(false)} disabled={withdrawing} />
            <Button label="Withdraw" icon="pi pi-undo" className="p-button-secondary" onClick={handleWithdraw} loading={withdrawing} />
        </div>
    );

    // ══════════════════════════════════════════
    // MAIN RENDER
    // ══════════════════════════════════════════

    return (
        <div className="grid claims-dashboard">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />

                    {/* Page Header - Compact */}
                    <div className="surface-card border-round shadow-1 p-2 mb-2">
                        <div className="flex align-items-center justify-content-between">
                            <h2 className="text-900 font-bold m-0" style={{ fontSize: '1rem' }}>Claims Dashboard</h2>
                        </div>
                    </div>

                    {/* TabView - Compact matching MyOffers */}
                    <TabView
                        activeIndex={activeTab}
                        onTabChange={(e) => setActiveTab(e.index)}
                        pt={{
                            navContainer: { style: { fontSize: '0.8rem' } },
                            nav: { style: { padding: '0' } },
                            inkbar: { style: { height: '2px' } }
                        }}
                    >

                        {/* TAB 1: RECEIVED CLAIMS */}
                        <TabPanel
                            header={receivedTabLabel}
                            pt={{ headerAction: { style: { padding: '0.5rem 0.85rem', fontSize: '0.8rem' } } }}
                        >
                            {renderTabContent({
                                data: rcData, loading: rcLoading, summary: rcSummary,
                                filter: rcFilter, setFilter: setRcFilter,
                                loanTypeFilter: rcLoanTypeFilter, setLoanTypeFilter: setRcLoanTypeFilter,
                                first: rcFirst, setFirst: setRcFirst, total: rcTotal,
                                loadFn: loadReceivedClaims,
                                expandedState: rcExpanded, setExpandedState: setRcExpanded,
                                offerIdxState: rcOfferIdx, setOfferIdxState: setRcOfferIdx,
                                searchInput: rcSearchInput, setSearchInput: setRcSearchInput,
                                searchValue: rcSearch, setSearchValue: setRcSearch,
                                dateFrom: rcDateFrom, setDateFrom: setRcDateFrom,
                                dateTo: rcDateTo, setDateTo: setRcDateTo,
                                mode: 'received', emptyIcon: 'pi-inbox',
                                emptyTitle: 'No Claims Received',
                                emptyMsg: 'No claims have been received yet. Claims will appear here when employees claim offers from your broadcast leads.',
                            })}
                        </TabPanel>

                        {/* TAB 2: SENT CLAIMS */}
                        <TabPanel
                            header={sentTabLabel}
                            pt={{ headerAction: { style: { padding: '0.5rem 0.85rem', fontSize: '0.8rem' } } }}
                        >
                            {renderTabContent({
                                data: scData, loading: scLoading, summary: scSummary,
                                filter: scFilter, setFilter: setScFilter,
                                loanTypeFilter: scLoanTypeFilter, setLoanTypeFilter: setScLoanTypeFilter,
                                first: scFirst, setFirst: setScFirst, total: scTotal,
                                loadFn: loadSentClaims,
                                expandedState: scExpanded, setExpandedState: setScExpanded,
                                offerIdxState: scOfferIdx, setOfferIdxState: setScOfferIdx,
                                searchInput: scSearchInput, setSearchInput: setScSearchInput,
                                searchValue: scSearch, setSearchValue: setScSearch,
                                dateFrom: scDateFrom, setDateFrom: setScDateFrom,
                                dateTo: scDateTo, setDateTo: setScDateTo,
                                mode: 'sent', emptyIcon: 'pi-send',
                                emptyTitle: 'No Claims Sent',
                                emptyMsg: 'You haven\'t claimed any offers yet. Go to Available Offers to claim broadcast offers.',
                            })}
                        </TabPanel>
                    </TabView>

                    {/* ═══ DIALOGS ═══ */}

                    {/* Approve Dialog */}
                    <Dialog
                        header={<div className="flex align-items-center gap-2"><i className={`pi ${isProcessorSwitching ? 'pi-sync' : 'pi-check-circle'} text-green-500`} /><span>{isProcessorSwitching ? 'Switch Processor' : 'Approve Claim'}</span></div>}
                        visible={approveDialogVisible}
                        style={{ width: '90vw', maxWidth: '520px' }}
                        modal
                        footer={approveDialogFooter}
                        onHide={() => setApproveDialogVisible(false)}
                    >
                        {approvingClaim && (
                            <div>
                                {isProcessorSwitching && <Message severity="warn" className="w-full mb-3" text="This will revoke the current processor's access and grant full access to the new processor." />}
                                <div className="surface-100 border-round p-3 mb-3">
                                    <div className="font-semibold text-900 mb-1">{approvingClaim.claimed_by?.name || 'Unknown Employee'}</div>
                                    {approvingClaim.claim_message && <div className="text-600 text-sm font-italic">"{approvingClaim.claim_message}"</div>}
                                </div>
                                {approvingOffer && (
                                    <div className="border-1 surface-border border-round p-3 mb-3">
                                        <div className="flex align-items-center gap-2 mb-2">
                                            <i className="pi pi-building text-primary" />
                                            <span className="font-bold text-900">{approvingOffer.bank_name || approvingOffer.Bank || 'Unknown Bank'}</span>
                                            {approvingOffer.product_name && <span className="text-500 text-sm">&middot; {approvingOffer.product_name}</span>}
                                        </div>
                                        <div className="grid text-sm">
                                            <div className="col-6"><div className="text-500 mb-1">Loan Amount</div><div className="font-bold">{
                                                approvingOffer.loan_amount ? formatCurrency(approvingOffer.loan_amount)
                                                : (approvingOffer.min_loan_amount || approvingOffer.max_loan_amount)
                                                    ? `${formatCurrency(approvingOffer.min_loan_amount)} - ${formatCurrency(approvingOffer.max_loan_amount)}`
                                                    : 'N/A'
                                            }</div></div>
                                            <div className="col-6"><div className="text-500 mb-1">EMI</div><div className="font-bold text-blue-600">{
                                                approvingOffer.emi ? formatCurrency(approvingOffer.emi) : 'N/A'
                                            }</div></div>
                                            <div className="col-6"><div className="text-500 mb-1">Rate</div><div className="font-bold">{approvingOffer.roi || approvingOffer.Rate_of_Interest || '-'}%</div></div>
                                            <div className="col-6"><div className="text-500 mb-1">Tenure</div><div className="font-bold">{
                                                approvingOffer.tenure ? `${approvingOffer.tenure} months`
                                                : (approvingOffer.min_tenure || approvingOffer.max_tenure)
                                                    ? `${approvingOffer.min_tenure} - ${approvingOffer.max_tenure} months`
                                                    : 'N/A'
                                            }</div></div>
                                        </div>
                                    </div>
                                )}
                                <p className="text-600 text-sm m-0">This will create a full-access share for this employee, allowing them to see all customer details and process the lead.</p>
                            </div>
                        )}
                    </Dialog>

                    {/* Reject Dialog */}
                    <Dialog header="Reject Claim" visible={rejectDialogVisible} style={{ width: '90vw', maxWidth: '480px' }} modal footer={rejectDialogFooter} onHide={() => setRejectDialogVisible(false)}>
                        {rejectingClaim && (
                            <div>
                                <div className="surface-100 border-round p-3 mb-3">
                                    <div className="font-semibold text-900 mb-1">{rejectingClaim.claimed_by?.name || 'Unknown'}</div>
                                    {rejectingClaim.claim_message && <div className="text-600 text-sm font-italic mb-2">"{rejectingClaim.claim_message}"</div>}
                                    {rejectingOffer && (
                                        <div className="flex align-items-center gap-2 text-sm mt-1 flex-wrap">
                                            <i className="pi pi-building text-500" />
                                            <span className="text-700">{rejectingOffer.bank_name || rejectingOffer.Bank || 'Unknown Bank'}</span>
                                            {rejectingOffer.roi && <Tag value={`${rejectingOffer.roi}%`} severity="info" className="text-xs" />}
                                            {rejectingOffer.emi ? <span className="text-600">{formatCurrency(rejectingOffer.emi)}/mo</span>
                                                : rejectingOffer.min_loan_amount ? <span className="text-600">{formatCurrency(rejectingOffer.min_loan_amount)} - {formatCurrency(rejectingOffer.max_loan_amount)}</span>
                                                : null}
                                        </div>
                                    )}
                                </div>
                                <div className="field">
                                    <label htmlFor="rejectReason" className="font-semibold block mb-2">Reason (optional)</label>
                                    <InputTextarea id="rejectReason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} rows={2} maxLength={1000} className="w-full" autoResize placeholder="Provide a reason for rejection..." />
                                </div>
                            </div>
                        )}
                    </Dialog>

                    {/* Withdraw Dialog */}
                    <Dialog
                        header={<div className="flex align-items-center gap-2"><i className="pi pi-undo text-500" /><span>Withdraw Claim</span></div>}
                        visible={withdrawDialogVisible}
                        style={{ width: '90vw', maxWidth: '480px' }}
                        modal
                        footer={withdrawDialogFooter}
                        onHide={() => setWithdrawDialogVisible(false)}
                    >
                        {withdrawingClaim && (
                            <div>
                                <p className="text-600 mb-3">Are you sure you want to withdraw your claim? This action cannot be undone.</p>
                                {withdrawingOffer && (
                                    <div className="border-1 surface-border border-round p-3 mb-3">
                                        <div className="flex align-items-center gap-2 mb-2">
                                            <i className="pi pi-building text-primary" />
                                            <span className="font-bold text-900">{withdrawingOffer.bank_name || withdrawingOffer.Bank || 'Unknown Bank'}</span>
                                            {withdrawingOffer.product_name && <span className="text-500 text-sm">&middot; {withdrawingOffer.product_name}</span>}
                                        </div>
                                        <div className="grid text-sm">
                                            <div className="col-6"><div className="text-500 mb-1">Loan Amount</div><div className="font-bold">{formatLoanAmount(withdrawingOffer.loan_amount || withdrawingOffer.Loan_Amount)}</div></div>
                                            <div className="col-6"><div className="text-500 mb-1">EMI</div><div className="font-bold text-blue-600">{formatCurrency(withdrawingOffer.emi || withdrawingOffer.EMI)}</div></div>
                                            <div className="col-6"><div className="text-500 mb-1">Rate</div><div className="font-bold">{withdrawingOffer.roi || withdrawingOffer.Rate_of_Interest || '-'}%</div></div>
                                            <div className="col-6"><div className="text-500 mb-1">Tenure</div><div className="font-bold">{withdrawingOffer.tenure || withdrawingOffer.Tenure || '-'} months</div></div>
                                        </div>
                                    </div>
                                )}
                                {withdrawingClaim.claim_message && (
                                    <div className="surface-100 border-round p-2">
                                        <div className="text-500 text-xs mb-1">Your Message</div>
                                        <div className="text-700 text-sm font-italic">"{withdrawingClaim.claim_message}"</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Dialog>

                    {/* Close Lead Confirmation Dialog */}
                    <Dialog
                        header={<div className="flex align-items-center gap-2"><i className="pi pi-times-circle text-red-500" /><span>Close Lead</span></div>}
                        visible={decisionDialogVisible}
                        style={{ width: '90vw', maxWidth: '420px' }}
                        modal
                        footer={
                            <div className="flex justify-content-end gap-2">
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setDecisionDialogVisible(false)} disabled={decidingLoading} />
                                <Button label="Close Lead" icon="pi pi-times" className="p-button-danger" onClick={handleProcessingDecision} loading={decidingLoading} />
                            </div>
                        }
                        onHide={() => setDecisionDialogVisible(false)}
                    >
                        <div>
                            <Message severity="warn" className="w-full mb-3" text="This will mark the lead as exhausted. No further processing will be possible." />
                            <p className="text-700 m-0">Are you sure you want to close this lead? This action is final.</p>
                        </div>
                    </Dialog>

                    {/* Make Processor — small confirm dialog */}
                    <Dialog
                        header={<div className="flex align-items-center gap-2"><i className="pi pi-sync" style={{ color: 'var(--orange-500)' }} /><span>Change Processor</span></div>}
                        visible={!!processorConfirmTarget}
                        style={{ width: '90vw', maxWidth: '420px' }}
                        modal
                        footer={
                            <div className="flex justify-content-end gap-2">
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setProcessorConfirmTarget(null)} disabled={changingProcessor} />
                                <Button label="Confirm" icon="pi pi-check" className="p-button-warning" onClick={handleChangeProcessor} loading={changingProcessor} />
                            </div>
                        }
                        onHide={() => setProcessorConfirmTarget(null)}
                    >
                        {processorConfirmTarget && (
                            <div className="flex align-items-start gap-3">
                                <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '1.5rem', marginTop: '2px' }} />
                                <div>
                                    <p className="m-0 mb-2">
                                        Assign <strong>{processorConfirmTarget.claim.claimed_by?.name || 'this employee'}</strong> as the new processor?
                                    </p>
                                    <p className="m-0 text-600 text-sm">
                                        The current processor's access will be revoked and full lead access will be granted to the new processor.
                                    </p>
                                </div>
                            </div>
                        )}
                    </Dialog>
                </div>

                {/* Regenerate Offers Dialog — secure, no lead edit access */}
                <RegenerateOffersDialog
                    visible={regenVisible}
                    onHide={() => { setRegenVisible(false); setRegenBroadcast(null); }}
                    broadcast={regenBroadcast}
                    toast={toast}
                    onSuccess={() => { loadReceivedClaims(); loadSentClaims(); }}
                />
            </div>

            {/* Compact overrides — matching MyOffers/MyPayouts sizing exactly */}
            <style>{`
                .claims-dashboard .p-calendar.p-inputtext-sm .p-inputtext {
                    font-size: 0.7rem !important;
                }
                .claims-dashboard .p-calendar.p-inputtext-sm .p-datepicker-trigger {
                    width: 2rem !important;
                    padding: 0 !important;
                }
                .claims-dashboard .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-button-icon,
                .claims-dashboard .p-calendar.p-inputtext-sm .p-datepicker-trigger .p-icon {
                    font-size: 0.75rem !important;
                    width: 0.75rem !important;
                    height: 0.75rem !important;
                }
                .claims-dashboard .p-paginator .p-paginator-element {
                    min-width: 1.5rem !important;
                    height: 1.5rem !important;
                    font-size: 0.65rem !important;
                }
                .claims-dashboard .p-tabview .p-tabview-nav li .p-tabview-nav-link {
                    padding: 0.5rem 0.85rem !important;
                    font-size: 0.8rem !important;
                }
                .claims-dashboard .p-tabview .p-tabview-panels {
                    padding: 0.75rem 0 0 0 !important;
                }
            `}</style>

            {/* Unified Status Update Dialog */}
            <StatusUpdateDialog
                leadId={statusDialogLeadId}
                currentStatus={statusDialogCurrentStatus}
                visible={statusDialogVisible}
                onHide={() => setStatusDialogVisible(false)}
                onSuccess={() => loadSentClaims(Math.floor(scFirst / ITEMS_PER_PAGE) + 1)}
                preselect={statusDialogPreselect}
                toastRef={toast}
                defaultBankId={statusDialogBankId}
                defaultBankName={statusDialogBankName}
                offerBanks={statusDialogOfferBanks}
            />
        </div>
    );
};

export default ReceivedClaims;
